<script setup lang="ts">
import { ArrowRightIcon, CheckIcon, XIcon } from 'lucide-vue-next'
import { useFollowUserMutation } from '~~/src/composables/mutations/user/useFollowUserMutation'
import { useUnfollowUserMutation } from '~~/src/composables/mutations/user/useUnfollowUserMutation'
import { useGetUserInformationsQuery } from '~~/src/composables/query/user/getUserInformations.query'
import type { UserProfile } from '~~/src/types/user'
import { Button, type ButtonVariants } from '~~/src/components/ui/button'
import { cn } from '~~/src/helpers'
import { Skeleton } from '~~/src/components/ui/skeleton'

const props = defineProps<IProps>()

defineOptions({
  name: 'UserFollowButton',
})

interface IProps extends /* @vue-ignore */ ButtonVariants {
  user: UserProfile
  small?: boolean
}

const { user } = toRefs(props)
const { firebaseUser, isLoading } = useFirebaseAuth()
const router = useRouter()

const { mutate: followUser, isPending: isFollowingLoading } = useFollowUserMutation()
const { mutate: unfollowUser, isPending: isUnfollowingLoading } = useUnfollowUserMutation()

const { data: userInfo } = useGetUserInformationsQuery({ usersIds: [user.value.id] }, {
  enabled: computed(() => !!firebaseUser.value),
})
const isFollowing = ref(false)

watch(userInfo, () => {
  isFollowing.value = userInfo.value?.length ? userInfo.value[0].isFollowed : false
})

async function handleFollow() {
  if (isLoading.value || !user.value)
    return

  if (!firebaseUser.value)
    return router.push(useGetLoginURL())

  if (isFollowing.value) {
    unfollowUser(user.value.id, {
      onSuccess: () => {
        isFollowing.value = false
      },
    })
    return
  }

  followUser(
    user.value.id,
    {
      onSuccess: () => {
        isFollowing.value = true
      },

    },
  )
}
</script>

<template>
  <Button
    variant="outline"
    size="no-padding"
    class="rounded-full group items-center hover:bg-softBlack hover:text-white "
    :class="cn(small ? 'pl-3 pr-1 py-1 text-[10px] font-inter font-700 leading-[160%] h-6'
      : 'pl-4 pr-1.5 py-1', $attrs.class ?? '')"
    :disabled="isLoading || isFollowingLoading || isUnfollowingLoading"
    v-bind="$props"
    @click="handleFollow"
  >
    <template v-if="!isLoading">
      <span class="group-hover:inline group-hover:visible  hidden invisible">
        {{ isFollowing ? 'Unfollow' : 'Follow' }}
      </span>
      <span class="group-hover:hidden group-hover:invisible">
        {{ isFollowing ? 'Following' : "Follow" }}
      </span>
      <span
        class="ml-a justify-self-end [&_svg]:3 [&_svg]:h-3 text-softBlack bg-softGray group-hover:bg-white group-hover:text-softBlack rounded-full flex items-center justify-center"
        :class="{
          'w-4 h-4 [&_svg]:w-1.25 [&_svg]:h-1.25': small,
        }"
      >
        <ArrowRightIcon class="group-hover:hidden group-hover:invisible" />
        <CheckIcon v-if="!isFollowing" class="group-hover:inline group-hover:visible  hidden invisible" />
        <XIcon v-else class="group-hover:inline group-hover:visible hidden invisible" />
      </span>
    </template>
    <template v-else>
      <Skeleton class="w-12 h-3.5 rounded-full" />
      <Skeleton class="w-4 h-4 rounded-full" />
    </template>
  </Button>
</template>
